import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { VeterinarianSettings } from 'src/app/settings/models/setting.model';
import {
  getBarns,
  getVeterinarianSettings,
} from 'src/app/settings/store/selectors/setting.selector';

import {
  AnimalGroupDataRow,
  CodelistDiagnosis,
  CodelistMedicine,
  CodelistQuantityUnit,
  CodelistVaccine,
  EditAnimalGroupMode,
  MedicationDataRow,
  SwitchDayDataRow,
  VaccinationDataRow,
} from './animal-group.model';

import { MedicationEditComponent } from './medication-edit/medication-edit.component';
import { SwitchDayEditComponent } from './switch-day-edit/switch-day-edit.component';
import { VaccinationEditComponent } from './vaccination-edit/vaccination-edit.component';
import { AnimalGroup, AnimalGroupBarnMapping, Barn, Cycle } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getSelectedCycle } from '../../store/selectors/cycle.selectors';
import { getBarnsInUse } from '../../store/selectors/day-old-chick.selectors';

@Component({
  selector: 'app-animal-group',
  templateUrl: './animal-group.component.html',
  styleUrls: ['./animal-group.component.scss'],
  imports: [TableComponent, SummaryCardComponent, MatButton, MatIcon, TranslatePipe],
})
export class AnimalGroupComponent implements OnInit, OnDestroy {
  private readonly locale = inject<LocaleService>(LocaleService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly dialogService = inject(DialogService);

  private readonly destroyed$ = new Subject<void>();

  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  barnsInUse$: Observable<Barn[]> = this.store.select(getBarnsInUse);
  allBarns$: Observable<Barn[]> = this.store.select(getBarns);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  locationCountry: string;

  switchDayDataRows$: Observable<SwitchDayDataRow[]>;

  // eslint-disable-next-line
  codelistVaccines: CodelistVaccine[] = require('src/assets/data/CodelistVaccines.json');
  vaccinationDataRows$: Observable<VaccinationDataRow[]>;

  // eslint-disable-next-line
  codelistMedicines: CodelistMedicine[] = require('src/assets/data/CodelistMedicines.json');
  // eslint-disable-next-line
  codelistDiagnoses: CodelistDiagnosis[] = require('src/assets/data/CodelistDiagnoses.json');
  // eslint-disable-next-line
  codelistQuantityUnits: CodelistQuantityUnit[] = require('src/assets/data/CodelistQuantityUnits.json');
  medicationDataRows$: Observable<MedicationDataRow[]>;

  veterinarianSettings$: Observable<VeterinarianSettings> =
    this.store.select(getVeterinarianSettings);
  veterinarianSettings: VeterinarianSettings;
  animalGroupBarnMappings: AnimalGroupBarnMapping[] = [];
  animalGroupBarnMappingsInfo: AnimalGroupBarnMapping[] = [];

  /* BarnMapping Table Config */
  tableConfigBarnMapping: TableConfig = {
    dataSource: new MatTableDataSource([] as AnimalGroupBarnMapping[]),
    selectColumn: false,
    pagination: false,
    columns: [
      {
        id: 'barnName',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('barn'),
      },
      {
        id: 'animalGroupBarnId',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('barn-item.animal-group-barn-id'),
      },
    ],
  };

  summaryCardActions: SummaryCardAction[] = [
    {
      id: '',
    },
  ];

  /* Base Table Config */
  private readonly animalGroupBaseTableConfig: TableConfig = {
    columns: [],
    pagination: false,
    dataSource: null,
    selectColumn: false,
    actionColumn: false,
    scrolling: true,
    disableCards: true,
    wrapperWidth: '100%',
    activeSort: {
      id: 'date',
      start: 'desc',
      disableClear: true,
    },
    actions: [],
  };

  /* SwitchDay Table Config */
  private readonly switchDayTableColumns = [
    {
      id: 'barnDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('barn'),
    },
    {
      id: 'phaseFrom',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('animal-group.phaseFrom'),
    },
    {
      id: 'phaseTo',
      type: TableColumnTypes.NUMBER,
      label: this.locale.translate('animal-group.phaseTo'),
    },
    {
      id: 'date',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('animal-group.date'),
    },
  ];

  tableConfigSwitchDay: TableConfig = {
    ...this.animalGroupBaseTableConfig,
    columns: this.switchDayTableColumns,
    actions: [
      ...this.animalGroupBaseTableConfig.actions,
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        callback: (row: SwitchDayDataRow) => this.openSwitchDaySlideIn(row),
        icon: 'edit',
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        callback: (row: SwitchDayDataRow) => this.deleteSwitchDay(row),
        icon: 'delete',
        disabled: () => false,
      },
    ],
    dataSource: new MatTableDataSource<SwitchDayDataRow>([]),
  };

  /* Vaccination Table Config */
  private readonly vaccinationTableColumns = [
    {
      id: 'barnDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('barn'),
    },
    {
      id: 'date',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('animal-group.date'),
    },
    {
      id: 'country',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('product-overview.country'),
    },
    {
      id: 'vaccineDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('animal-group.vaccine'),
    },
  ];

  tableConfigVaccination: TableConfig = {
    ...this.animalGroupBaseTableConfig,
    columns: this.vaccinationTableColumns,
    actions: [
      ...this.animalGroupBaseTableConfig.actions,
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        callback: (row: VaccinationDataRow) => this.openVaccinationSlideIn(row),
        icon: 'edit',
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        callback: (row: VaccinationDataRow) => this.deleteVaccination(row),
        icon: 'delete',
        disabled: () => false,
      },
    ],
    dataSource: new MatTableDataSource<VaccinationDataRow>([]),
  };

  /* Medication Table Config */
  private readonly medicationTableColumns = [
    {
      id: 'barnDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('barn'),
    },
    {
      id: 'startDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('animal-group.treatmentDateStart'),
    },
    {
      id: 'endDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('animal-group.treatmentDateEnd'),
    },
    {
      id: 'country',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('product-overview.country'),
    },
    {
      id: 'medicineDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('animal-group.medication'),
    },
    {
      id: 'diagnosisDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('animal-group.diagnose'),
    },
    {
      id: 'quantityUnitDisplay',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('animal-group.quantity'),
    },
  ];

  tableConfigMedication: TableConfig = {
    ...this.animalGroupBaseTableConfig,
    columns: this.medicationTableColumns,
    actions: [
      ...this.animalGroupBaseTableConfig.actions,
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        callback: (row: MedicationDataRow) => this.openMedicationSlideIn(row),
        icon: 'edit',
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        callback: (row: MedicationDataRow) => this.deleteMedication(row),
        icon: 'delete',
        disabled: () => false,
      },
    ],
    dataSource: new MatTableDataSource<MedicationDataRow>([]),
  };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    combineLatest([this.barnsInUse$, this.allBarns$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([barnsInUse, allBarns]) => {
        if (barnsInUse && allBarns) {
          let allAnimalGroupMappings: AnimalGroupBarnMapping[] = [];
          this.animalGroupBarnMappingsInfo = [];

          barnsInUse.forEach((barnInUse) => {
            const animalGroupMappingsForCurrentBarn = this.getAnimalGroupMappingsForBarn(
              barnInUse,
              allBarns,
            );
            allAnimalGroupMappings = allAnimalGroupMappings.concat(
              animalGroupMappingsForCurrentBarn,
            );

            if (animalGroupMappingsForCurrentBarn.length > 0) {
              animalGroupMappingsForCurrentBarn.forEach((mapping) => {
                this.animalGroupBarnMappingsInfo.push({
                  barnName: barnInUse.name,
                  animalGroupBarnId: mapping.animalGroupBarnId,
                } as AnimalGroupBarnMapping);
              });
            } else {
              this.animalGroupBarnMappingsInfo.push({
                barnName: barnInUse.name,
                animalGroupBarnId: this.locale.translate('barn-item.not-mapped'),
              } as AnimalGroupBarnMapping);
            }
          });

          this.tableConfigBarnMapping.dataSource = new MatTableDataSource(
            this.animalGroupBarnMappingsInfo,
          );
          this.animalGroupBarnMappings = allAnimalGroupMappings;
        }
      });

    this.veterinarianSettings$.pipe(take(1)).subscribe((veterinarianSettings) => {
      this.veterinarianSettings = veterinarianSettings;
    });

    this.location$.pipe(takeUntil(this.destroyed$)).subscribe((loc) => {
      this.locationCountry = loc?.country;
    });

    this.vaccinationDataRows$ = this.cycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        const vaccinationDataRows: VaccinationDataRow[] = (
          selectedCycle?.animalGroup?.vaccinations ?? []
        ).map((row) => ({
          ...row,
          vaccineDisplay: row.vaccine?.name,
          barnDisplay: row.barn?.barnName + ' - ' + row.barn?.animalGroupBarnId,
        }));

        return vaccinationDataRows;
      }),
    );

    this.medicationDataRows$ = this.cycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        const medicationDataRows: MedicationDataRow[] = (
          selectedCycle?.animalGroup?.medications ?? []
        ).map((row) => {
          const quantityUnit = this.codelistQuantityUnits.find(
            (unit) => unit.codeUnit === row.appliedQuantity?.unitCode,
          );

          return {
            ...row,
            medicineDisplay: row.medicine?.name,
            diagnosisDisplay:
              row.diagnoseName ??
              this.codelistDiagnoses.find((diagnosis) => diagnosis.code === row.diagnoseCode)?.name,
            quantityUnitDisplay: [row.appliedQuantity?.measuredValue, quantityUnit?.unit]
              .filter((value) => value !== null && value !== undefined)
              .join(' '),
            barnDisplay: row.barn?.barnName + ' - ' + row.barn?.animalGroupBarnId,
          };
        });

        return medicationDataRows;
      }),
    );

    this.switchDayDataRows$ = this.cycle$.pipe(
      takeUntil(this.destroyed$),
      map((selectedCycle) => {
        const switchDayDataRows: SwitchDayDataRow[] = (
          selectedCycle?.animalGroup?.switchDays ?? []
        ).map((row) => ({
          ...row,
          barnDisplay: row.barn?.barnName + ' - ' + row.barn?.animalGroupBarnId,
        }));

        return switchDayDataRows;
      }),
    );
  }

  ngOnInit(): void {
    this.switchDayDataRows$.pipe(takeUntil(this.destroyed$)).subscribe((switchDayDataRows) => {
      this.tableConfigSwitchDay = {
        ...this.tableConfigSwitchDay,
        dataSource: new MatTableDataSource<SwitchDayDataRow>(switchDayDataRows),
      };
    });

    this.vaccinationDataRows$.pipe(takeUntil(this.destroyed$)).subscribe((vaccinationDataRows) => {
      this.tableConfigVaccination = {
        ...this.tableConfigVaccination,
        dataSource: new MatTableDataSource<VaccinationDataRow>(vaccinationDataRows),
      };
    });

    this.medicationDataRows$.pipe(takeUntil(this.destroyed$)).subscribe((medicationDataRows) => {
      this.tableConfigMedication = {
        ...this.tableConfigMedication,
        dataSource: new MatTableDataSource<MedicationDataRow>(medicationDataRows),
      };
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getDistinctCountries(codelistVaccines: CodelistVaccine[]): string[] {
    const countries = codelistVaccines.map((vaccine) => vaccine.country);
    const distinctCountries = Array.from(new Set(countries));
    return distinctCountries;
  }

  getAnimalGroupMappingsForBarn(cycleBarn: Barn, settingsBarns: Barn[]): AnimalGroupBarnMapping[] {
    const result: AnimalGroupBarnMapping[] = [];
    const settingsBarn = settingsBarns.find((bs) => cycleBarn.id === bs.id);

    if (settingsBarn === undefined) {
      return result;
    }

    if (settingsBarn.animalGroupBarnIds && settingsBarn.animalGroupBarnIds.length > 0) {
      settingsBarn.animalGroupBarnIds.forEach((animalGroupBarnId) => {
        result.push({
          barnId: cycleBarn.id,
          barnName: cycleBarn.name,
          animalGroupBarnId,
        } as AnimalGroupBarnMapping);
      });
    }

    return result;
  }

  deleteAnimalGroupItem(row: VaccinationDataRow) {
    this.deleteVaccination(row);
  }

  openVaccinationSlideIn(vaccinationRow: VaccinationDataRow = null) {
    const editMode = vaccinationRow !== null ? EditAnimalGroupMode.Edit : EditAnimalGroupMode.Add;

    this.dialogService
      .fullscreenDialog(VaccinationEditComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate(editMode),
        pushHistoryState: false,
        mode: editMode,
        barnMappings: this.animalGroupBarnMappings,
        countries: this.getDistinctCountries(this.codelistVaccines),
        codelistVaccines: this.codelistVaccines,
        locationCountry: this.locationCountry,
        vaccinationRow,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ result }) => {
        if (result != null) {
          this.saveVaccination(result.vaccinationRow);
        }
      });
  }

  saveVaccination(vaccinationRow: VaccinationDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const animalGroup = this.getOrCreateCycleAnimalGroup(cycle);
      const vaccinations = animalGroup.vaccinations.filter((v) => v.id !== vaccinationRow.id);
      const id = vaccinationRow.id ?? this.getNextId(vaccinations);

      const animalGroupUpdate = {
        ...animalGroup,
        vaccinations: [
          ...vaccinations,
          {
            id,
            barn: vaccinationRow.barn,
            country: vaccinationRow.country,
            date: vaccinationRow.date,
            vaccine: vaccinationRow.vaccine,
          },
        ],
        veterinarianId: this.veterinarianSettings?.id,
        barnMappings: this.animalGroupBarnMappings,
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  deleteVaccination(vaccinationRow: VaccinationDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const vaccinations = cycle.animalGroup.vaccinations.filter((v) => v.id !== vaccinationRow.id);

      const animalGroupUpdate = {
        ...cycle.animalGroup,
        vaccinations: [...vaccinations],
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  openMedicationSlideIn(medicationRow: MedicationDataRow = null) {
    const editMode = medicationRow !== null ? EditAnimalGroupMode.Edit : EditAnimalGroupMode.Add;

    this.dialogService
      .fullscreenDialog(MedicationEditComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate(editMode),
        pushHistoryState: false,
        mode: editMode,
        barnMappings: this.animalGroupBarnMappings,
        countries: this.getDistinctCountries(this.codelistMedicines),
        codelistMedicines: this.codelistMedicines,
        codelistDiagnoses: this.codelistDiagnoses,
        codelistQuantityUnits: this.codelistQuantityUnits,
        locationCountry: this.locationCountry,
        medicationRow,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ result }) => {
        if (result != null) {
          this.saveMedication(result.medicationRow);
        }
      });
  }

  saveMedication(medicationRow: MedicationDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const animalGroup = this.getOrCreateCycleAnimalGroup(cycle);
      const medications = animalGroup.medications.filter((v) => v.id !== medicationRow.id);
      const id = medicationRow.id ?? this.getNextId(medications);

      const animalGroupUpdate = {
        ...animalGroup,
        medications: [
          ...medications,
          {
            id,
            barn: medicationRow.barn,
            country: medicationRow.country,
            startDate: medicationRow.startDate,
            endDate: medicationRow.endDate,
            medicine: medicationRow.medicine,
            diagnoseCode: medicationRow.diagnoseCode,
            diagnoseName: medicationRow.diagnoseName,
            appliedQuantity: medicationRow.appliedQuantity,
          },
        ],
        veterinarianId: this.veterinarianSettings?.id,
        barnMappings: this.animalGroupBarnMappings,
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  deleteMedication(medicationRow: MedicationDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const medications = cycle.animalGroup.medications.filter((v) => v.id !== medicationRow.id);

      const animalGroupUpdate = {
        ...cycle.animalGroup,
        medications: [...medications],
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  openSwitchDaySlideIn(switchDayRow: SwitchDayDataRow = null) {
    const editMode = switchDayRow !== null ? EditAnimalGroupMode.Edit : EditAnimalGroupMode.Add;

    this.dialogService
      .fullscreenDialog(SwitchDayEditComponent, {
        confirmButton: true,
        confirmButtonText: this.locale.translate(editMode),
        pushHistoryState: false,
        mode: editMode,
        barnMappings: this.animalGroupBarnMappings,
        switchDayRow,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ result }) => {
        if (result != null) {
          this.saveSwitchDay(result.switchDayRow);
        }
      });
  }

  saveSwitchDay(switchDayRow: SwitchDayDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const animalGroup = this.getOrCreateCycleAnimalGroup(cycle);
      const switchDays = animalGroup.switchDays.filter((v) => v.id !== switchDayRow.id);
      const id = switchDayRow?.id ?? this.getNextId(switchDays);

      const animalGroupUpdate = {
        ...animalGroup,
        switchDays: [
          ...switchDays,
          {
            id,
            barn: switchDayRow.barn,
            phaseFrom: switchDayRow.phaseFrom,
            phaseTo: switchDayRow.phaseTo,
            date: switchDayRow.date,
          },
        ],
        veterinarianId: this.veterinarianSettings?.id,
        barnMappings: this.animalGroupBarnMappings,
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  deleteSwitchDay(switchDayRow: SwitchDayDataRow) {
    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      const switchDays = cycle.animalGroup.switchDays.filter((v) => v.id !== switchDayRow.id);

      const animalGroupUpdate = {
        ...cycle.animalGroup,
        switchDays: [...switchDays],
      };

      this.saveCycle(cycle.id, animalGroupUpdate);
    });
  }

  saveCycle(cycleId: number, animalGroupUpdate: AnimalGroup) {
    this.store.dispatch(
      cycleActions.upsertAnimalGroup({
        cycleId,
        animalGroup: animalGroupUpdate,
      }),
    );
  }

  private getNextId(rows: AnimalGroupDataRow[]): number {
    return rows.length > 0 ? Math.max(...rows.map((row: AnimalGroupDataRow) => row.id)) + 1 : 1;
  }

  private getOrCreateCycleAnimalGroup(selectedCycle: Cycle): AnimalGroup {
    const animalGroup: AnimalGroup = selectedCycle.animalGroup ?? ({} as AnimalGroup);
    return {
      ...animalGroup,
      vaccinations: animalGroup?.vaccinations ?? [],
    };
  }
}
